import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import {
    type DefaultValues,
    type SubmitHandler,
    useForm,
} from 'react-hook-form';
import type { z } from 'zod';
import { getFormNames } from '../utils/form/getFormNames';

export const useMyForm = <V extends object, DV extends V>({
    schema,
    defaultValues,
    submitHandler,
    reactiveValues,
}: {
    schema: z.Schema<V, V>;
    defaultValues: DV;
    submitHandler?: SubmitHandler<NoInfer<DV>>;
    reactiveValues?: DV | null;
}) => {
    const form = useForm<DV>({
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        defaultValues: (reactiveValues ?? defaultValues) as DefaultValues<DV>,
        resolver: async (data, context, options) => {
            const validation = await zodResolver(
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                schema as z.Schema<DV, DV>
            )(data, context, options);
            // uncomment to debug form validation
            // console.log({ data, errors: validation.errors });
            return validation;
        },
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        values: reactiveValues ?? undefined,
    });

    const formNames = useMemo(
        () => getFormNames(defaultValues),
        [defaultValues]
    );

    const handleSubmit = form.handleSubmit;
    const onSubmit = useMemo(
        () => (submitHandler ? handleSubmit(submitHandler) : undefined),
        [handleSubmit, submitHandler]
    );

    return {
        form,
        formNames,
        onSubmit, // TODO type-safety: submitHandler <-> onSubmit
    };
};
